import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics } from '@/mocks/comics';

const TABS = ['全部', '连载中', '已完结', '即将上线'] as const;

export default function Progress() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || '全部';

  const list = useMemo(() => {
    if (status === '全部') return comics;
    return comics.filter((c) => c.status === status);
  }, [status]);

  const setTab = (s: string) => {
    const next = new URLSearchParams(params);
    if (s === '全部') next.delete('status'); else next.set('status', s);
    setParams(next, { replace: true });
  };

  const accentStyle = (c: { color: string }) => c.color;

  return (
    <>
      <Seo
        title="连载进度 - 韩国漫画大全 · 实时同步"
        description="韩国漫画大全连载进度实时同步，连载中、已完结、即将上线一目了然，追更进度不错过任何一话。"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '韩国漫画大全 · 连载进度',
            inLanguage: 'zh-CN',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: '韩国漫画大全连载进度列表',
            itemListElement: comics.map((c, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: c.title,
            })),
          },
        ]}
      />
      <Navbar />

      <main className="mx-auto max-w-5xl px-4 pb-16 pt-24 md:px-6 md:pb-24 md:pt-32">
        <nav aria-label="面包屑" className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-800">连载进度</span>
        </nav>

        <div className="mb-8">
          <span className="text-xs font-medium tracking-wide text-accent-600">PROGRESS</span>
          <h1 className="mt-1 font-heading text-3xl text-foreground-950 md:text-4xl">连载进度</h1>
          <p className="mt-2 text-sm text-foreground-600">
            连载进度同步更新，追更每一话不错过 · 共收录 <strong className="text-accent-600">{comics.length}</strong> 部作品
          </p>
        </div>

        {/* Status tabs */}
        <div className="mb-6 flex flex-wrap gap-2 rounded-full bg-background-100 p-1 md:inline-flex">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                status === t ? 'bg-accent-500 font-medium text-white' : 'text-foreground-700 hover:text-accent-600'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Progress list */}
        <div className="space-y-3">
          {list.map((c) => (
            <article key={c.id} className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-300 md:p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <img
                  src={c.cover}
                  alt={`${c.title} 连载进度`}
                  className="h-20 w-14 shrink-0 rounded-md object-cover object-top"
                />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-foreground-900">{c.title}</h2>
                    <span className="whitespace-nowrap rounded-full bg-background-100 px-2.5 py-0.5 text-xs text-foreground-500">{c.genre}</span>
                    <span className="whitespace-nowrap text-xs text-foreground-500">{c.chapter}</span>
                  </div>
                  <p className="mt-1 text-xs text-foreground-600">{c.tagline}</p>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-background-200">
                      <div className={`h-full rounded-full ${accentStyle(c)}`} style={{ width: `${c.progress}%` }}></div>
                    </div>
                    <span className="whitespace-nowrap text-xs text-foreground-600">进度 {c.progress}%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 md:w-40 md:flex-col md:items-end">
                  <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">{c.status}</span>
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-refresh-line"></i>{c.updateTime}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Sync note */}
        <div className="mt-8 flex flex-col items-start gap-3 rounded-lg bg-accent-100/70 p-5 sm:flex-row sm:items-center">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-500 text-white">
            <i className="ri-verified-badge-fill text-xl"></i>
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground-900">连载进度实时同步</p>
            <p className="mt-1 text-sm text-foreground-700">
              无论新作上线还是老番更新，韩国漫画大全都会在第一时间同步最新话数与更新时间，让你追更不停歇。
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}